import { type UnitPerformanceDataResponseEntry } from '../../../../api/customEndpoints/fetchUnitPerformanceScores';
import { type UseQueryResult } from '@tanstack/react-query';
import { type PropsWithChildren } from 'react';
import { useTranslate } from 'ra-core';
import { Card, CardContent } from '../../../../components/ui/card';
import { Separator } from '../../../../components/ui/separator';
import { Error } from '../../../../components/admin/error';
import { Loading } from '../../../../components/admin/loading';
import { KpiPerformanceBadge } from '../../../Kpis/KpiData/KpiPerformanceBadge';
import { formatDate, formatNumber } from '../formatters';

export function KPIPerformanceOverviewCard({
  kpiData,
}: {
  kpiData: UseQueryResult<UnitPerformanceDataResponseEntry[]>;
}) {
  const translate = useTranslate();

  if (kpiData.isPending) {
    return <Loading />;
  } else if (kpiData.isError) {
    return (
      <Error
        resetErrorBoundary={() => {
          void kpiData.refetch();
        }}
        error={kpiData.error}
      />
    );
  }

  return (
    <CardContent>
      <h2 className="text-xl font-semibold">
        {translate('moonstar.unitPerformance.kpiOverview.title')}
      </h2>

      <div className="mt-4 space-y-4">
        {kpiData.data.map((kpi) => (
          <Card key={kpi.id}>
            <CardContent>
              <h3 className="text-lg font-medium">{kpi.name}</h3>
              <div className="mt-2 space-y-2">
                <KpiValueRow>
                  <KpiValueRowEntry>
                    {translate(
                      'moonstar.unitPerformance.kpiOverview.performanceSummary'
                    )}
                  </KpiValueRowEntry>
                  <KpiValueRowEntry>
                    {translate(
                      'moonstar.unitPerformance.kpiOverview.previousSummary'
                    )}
                  </KpiValueRowEntry>
                </KpiValueRow>
                <KpiValueRow>
                  <KpiValueRowEntry
                    divider
                    label={translate(
                      'moonstar.unitPerformance.kpiOverview.target'
                    )}
                  >
                    {formatNumber(kpi.latestTarget)}
                  </KpiValueRowEntry>
                  <KpiValueRowEntry
                    divider
                    label={translate(
                      'moonstar.unitPerformance.kpiOverview.previousTarget'
                    )}
                  >
                    {formatNumber(kpi.previousTarget)}
                  </KpiValueRowEntry>
                </KpiValueRow>
                <KpiValueRow>
                  <KpiValueRowEntry
                    divider
                    label={translate(
                      'moonstar.unitPerformance.kpiOverview.actual'
                    )}
                  >
                    {formatNumber(kpi.latestActual)}
                  </KpiValueRowEntry>
                  <KpiValueRowEntry
                    divider
                    label={translate(
                      'moonstar.unitPerformance.kpiOverview.previousActual'
                    )}
                  >
                    {formatNumber(kpi.previousActual)}
                  </KpiValueRowEntry>
                </KpiValueRow>
                <KpiValueRow>
                  <KpiValueRowEntry
                    divider
                    label={translate(
                      'moonstar.unitPerformance.kpiOverview.performance'
                    )}
                  >
                    {kpi.latestPerformance === undefined ? undefined : (
                      <KpiPerformanceBadge
                        value={parseFloat(kpi.latestPerformance)}
                      />
                    )}
                  </KpiValueRowEntry>
                  <KpiValueRowEntry
                    divider
                    label={translate(
                      'moonstar.unitPerformance.kpiOverview.previousPerformance'
                    )}
                  >
                    {kpi.previousPerformance === undefined ? undefined : (
                      <KpiPerformanceBadge
                        value={parseFloat(kpi.previousPerformance)}
                      />
                    )}
                  </KpiValueRowEntry>
                </KpiValueRow>
                <KpiValueRow>
                  <KpiValueRowEntry
                    label={translate(
                      'moonstar.unitPerformance.kpiOverview.date'
                    )}
                  >
                    {formatDate(kpi.latestCreatedOn)}
                  </KpiValueRowEntry>
                  <KpiValueRowEntry
                    label={translate(
                      'moonstar.unitPerformance.kpiOverview.date'
                    )}
                  >
                    {formatDate(kpi.previousCreatedOn)}
                  </KpiValueRowEntry>
                </KpiValueRow>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </CardContent>
  );
}

function KpiValueRow({ children }: PropsWithChildren) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

function KpiValueRowEntry({
  children,
  divider,
  label,
}: PropsWithChildren<{ divider?: boolean; label?: string }>) {
  const translate = useTranslate();

  return (
    <div className="min-w-0">
      <div className="flex min-h-8 items-center justify-between gap-2 text-sm leading-8">
        {label ? <span>{label}:</span> : null}
        <span className="font-bold">
          {children ?? translate('moonstar.unitPerformance.notAvailable')}
        </span>
      </div>
      {divider ? <Separator /> : null}
    </div>
  );
}
